import { getInitials, getShortName } from '@/utils/userNames'
import Modal from '@components/Modal'
import { GetUserType } from '@orbit/api-types'
import React, { useEffect, useMemo, useState } from 'react'
import {
    Button,
    ButtonGroup,
    Checkbox,
    PickerAvatar,
    PickerList,
    PickerName,
    PickerRow,
} from './MultiUserPickerModal.styles'

interface MultiUserPickerModalProps {
    isOpen: boolean
    onClose: () => void
    onConfirm: (userIds: number[]) => void
    title: string
    users: GetUserType[]
    currentUserId?: number
    initiallySelectedUserIds: number[]
}

const Avatar: React.FC<{ user: GetUserType }> = ({ user }) => (
    <PickerAvatar>
        {user.profilePicture ? (
            <img src={user.profilePicture} alt={getInitials(user)} />
        ) : (
            getInitials(user)
        )}
    </PickerAvatar>
)

const MultiUserPickerModal: React.FC<MultiUserPickerModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    users,
    currentUserId,
    initiallySelectedUserIds,
}) => {
    const [selected, setSelected] = useState<Set<number>>(() => new Set(initiallySelectedUserIds))

    useEffect(() => {
        if (isOpen) {
            setSelected(new Set(initiallySelectedUserIds))
        }
    }, [isOpen, initiallySelectedUserIds])

    const sortedUsers = useMemo(() => {
        return [...users].sort((a, b) => {
            if (currentUserId != null) {
                if (a.id === currentUserId) return -1
                if (b.id === currentUserId) return 1
            }
            return 0
        })
    }, [users, currentUserId])

    const toggle = (id: number) => {
        setSelected(prev => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }

    const handleConfirm = () => {
        onConfirm(Array.from(selected))
    }

    const handleClear = () => {
        setSelected(new Set())
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            size="medium"
            backgroundColor="#fff8f6"
            footer={
                <ButtonGroup>
                    <Button type="button" $variant="ghost" onClick={handleClear}>
                        Clear
                    </Button>
                    <Button type="button" $variant="primary" onClick={handleConfirm}>
                        Confirm
                    </Button>
                </ButtonGroup>
            }
        >
            <PickerList>
                {sortedUsers.map(user => {
                    const isSelf = currentUserId != null && user.id === currentUserId
                    const isSelected = selected.has(user.id)
                    return (
                        <PickerRow
                            key={user.id}
                            type="button"
                            $selected={isSelected}
                            onClick={() => toggle(user.id)}
                        >
                            <Checkbox $checked={isSelected}>{isSelected ? '✓' : ''}</Checkbox>
                            <Avatar user={user} />
                            <PickerName>
                                {getShortName(user)}
                                {isSelf ? ' (You)' : ''}
                            </PickerName>
                        </PickerRow>
                    )
                })}
            </PickerList>
        </Modal>
    )
}

export default MultiUserPickerModal
